import editorJsHtml from 'editorjs-html';
import { EditorJsRenderProps } from './types';
const EditorJsToHtml = editorJsHtml();
import { Box, Flex, Stack, Text, Title, useMantineTheme } from '@mantine/core';
import Link from 'next/link';
import Image from 'next/image';

enum EDITORJS_ITEMS_TYPES {
  PARAGRAPH = 'paragraph',
  IMAGE = 'image',
  LINK_TOOL = 'linkTool',
  HEADER = 'header',
  QUOTE = 'quote',
  LIST = 'list',
}

export const EditorJsRender = ({ data, linkColor }: EditorJsRenderProps) => {
  const theme = useMantineTheme();
  const resolvedLinkColor = linkColor ?? theme.colors.blue[7];

  const renderFormattedText = (text: string, index: number) => {
    return (
      <p
        key={index}
        style={{ margin: 0 }}
        dangerouslySetInnerHTML={{
          __html: text,
        }}
      />
    );
  };

  const applyLinkStyles = (html: string) => {
    const styledHTML = html.replace(
      /<a /g,
      `<a style="text-decoration: underline; color: ${resolvedLinkColor}" `
    );

    return styledHTML;
  };

  return (
    <Flex direction="column">
      {data.blocks.map((item) => {
        if (item.type === EDITORJS_ITEMS_TYPES.IMAGE) {
          return (
            <Box key={item.id} w="100%" my={24}>
              <Image
                style={{ objectFit: 'contain' }}
                src={item.data.file.url}
                alt={item.data.caption}
                layout="responsive"
                sizes="(max-width: 768px) 900px, 1366px"
                width={100}
                height={100}
              />
            </Box>
          );
        }

        if (item.type === EDITORJS_ITEMS_TYPES.LINK_TOOL) {
          return (
            <Link
              key={item.id}
              href={item.data.link}
              style={{ textDecoration: 'none', marginTop: 8, marginBottom: 8 }}
            >
              <Text variant="bodyLink"> {item.data.meta.title}</Text>
            </Link>
          );
        }

        if (item.type === EDITORJS_ITEMS_TYPES.HEADER) {
          switch (item.data.level) {
            case 1:
              return (
                <Title key={item.id} variant="h1" order={1} py={4}>
                  {item.data.text}
                </Title>
              );
            case 2:
              return (
                <Title key={item.id} variant="h2" order={2} py={4}>
                  {item.data.text}
                </Title>
              );
            case 3:
              return (
                <Title key={item.id} variant="h3" order={3} py={4}>
                  {item.data.text}
                </Title>
              );
            default:
              return (
                <Title key={item.id} variant="h1" order={1} py={4}>
                  {item.data.text}
                </Title>
              );
          }
        }

        if (item.type === EDITORJS_ITEMS_TYPES.QUOTE) {
          return (
            <Stack key={item.id} spacing={0} my={10}>
              <Text variant="bodyBold">{`"${item.data.text}"`}</Text>
              <Text variant="caption2" ml={16}>{`- ${item.data.caption}`}</Text>
            </Stack>
          );
        }

        if (item.type === EDITORJS_ITEMS_TYPES.PARAGRAPH) {
          const textParts: string[] = item.data.text.split('<br>');

          return (
            <Stack key={item.id} spacing={0} my={10}>
              {textParts.map((textPart, index) => {
                const styledHTML = applyLinkStyles(textPart);

                const renderedText = renderFormattedText(styledHTML, index);

                return (
                  <Flex direction="column" gap={10} key={index}>
                    <Text variant="body">{renderedText}</Text>
                    {index !== textParts.length - 1 && <Box />}
                  </Flex>
                );
              })}
            </Stack>
          );
        }

        if (item.type === EDITORJS_ITEMS_TYPES.LIST && item.data.items) {
          return (
            <Box key={item.id}>
              <ul style={{ marginTop: 0 }}>
                {item.data.items.map((listItem: string, index: number) => {
                  const textParts: string[] = listItem.split('<br>');

                  return (
                    <li key={index}>
                      {textParts.map((textPart, index) => {
                        const styledHTML = applyLinkStyles(textPart);

                        const renderedText = renderFormattedText(
                          styledHTML,
                          index
                        );

                        return (
                          <Flex direction="column" gap={10} key={index}>
                            <Text variant="body">{renderedText}</Text>
                            {index !== textParts.length - 1 && <Box />}
                          </Flex>
                        );
                      })}
                    </li>
                  );
                })}
              </ul>
            </Box>
          );
        }

        const html = EditorJsToHtml?.parseBlock(item);

        return <Box key={item.id} dangerouslySetInnerHTML={{ __html: html }} />;
      })}
    </Flex>
  );
};

export const EditorJsRenderLiveBlog = ({
  data,
  linkColor,
}: EditorJsRenderProps) => {
  const theme = useMantineTheme();
  const resolvedLinkColor = linkColor ?? theme.colors.blue[7];

  const renderFormattedText = (text: string, index: number) => {
    return (
      <p
        key={index}
        style={{ margin: 0 }}
        dangerouslySetInnerHTML={{
          __html: text,
        }}
      />
    );
  };

  const applyLinkStyles = (html: string) => {
    const styledHTML = html.replace(
      /<a /g,
      `<a style="text-decoration: underline; color: ${resolvedLinkColor}" `
    );

    return styledHTML;
  };

  return (
    <Flex direction="column">
      {data.blocks.map((item) => {
        if (item.type === EDITORJS_ITEMS_TYPES.LINK_TOOL) {
          return (
            <Link
              key={item.id}
              href={item.data.link}
              style={{ textDecoration: 'none', marginTop: 8, marginBottom: 8 }}
            >
              <Text variant="bodyLink"> {item.data.meta.title}</Text>
            </Link>
          );
        }

        if (item.type === EDITORJS_ITEMS_TYPES.PARAGRAPH) {
          const textParts: string[] = item.data.text.split('<br>');

          return (
            <Stack key={item.id} spacing={0} my={10}>
              {textParts.map((textPart, index) => {
                const styledHTML = applyLinkStyles(textPart);

                const renderedText = renderFormattedText(styledHTML, index);

                return (
                  <Flex direction="column" gap={10} key={index}>
                    <Text variant="body">{renderedText}</Text>
                    {index !== textParts.length - 1 && <Box />}
                  </Flex>
                );
              })}
            </Stack>
          );
        }

        const html = EditorJsToHtml?.parseBlock(item);

        return <Box key={item.id} dangerouslySetInnerHTML={{ __html: html }} />;
      })}
    </Flex>
  );
};
